import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import { AlertTriangle, ArrowLeft, ArrowRight, RotateCcw, Lock, Trophy } from "lucide-react";
import Nav from "../Components/Nav/Nav";
import Confetti from "../Components/FormationLearner/Confetti";
import CurriculumSidebar from "../Components/FormationLearner/CurriculumSidebar";
import Login from "./Login";
import { ToastContainer, toast } from "react-toastify";
import { UidContext } from "../Components/App.Context";
import { getLearnFormation, attemptQuiz } from "../actions/formationLearner.actions";

const sortByOrder = (arr) => [...(arr || [])].sort((a, b) => a.order - b.order);

const FormationQuiz = ({ socket }) => {
  const { id, quizId } = useParams();
  const uid = useContext(UidContext);
  const dispatch = useDispatch();
  const { current } = useSelector((s) => s.formationLearnerReducer);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [retake, setRetake] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoadError(false);
    dispatch(getLearnFormation(id)).catch(() => {
      if (active) setLoadError(true);
    });
    return () => {
      active = false;
    };
  }, [dispatch, id]);

  // La route /formations/:id/quizz/:quizId réutilise la même instance du composant :
  // on remet à zéro l'état de la tentative en cours quand le quizz change.
  useEffect(() => {
    setResult(null);
    setAnswers({});
    setRetake(false);
    setCelebrate(false);
  }, [quizId]);

  if (!uid) return <Login />;

  if (!current || !current.formation || String(current.formation.id) !== String(id)) {
    if (loadError) {
      return (
        <>
          <Nav socket={socket} />
          <div className="mainContainer">
            <div className="lesson-fullscreen">
              <div className="quiz-locked-notice">
                <AlertTriangle size={40} />
                <p>Formation indisponible. Elle a peut-être été dépubliée ou supprimée.</p>
                <Link to="/formations" className="greenButton">
                  <ArrowLeft size={15} /> Retour aux formations
                </Link>
              </div>
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        <Nav socket={socket} />
        <div className="mainContainer">
          <div className="lesson-fullscreen">
            <p style={{ padding: "2rem" }}>Chargement…</p>
          </div>
        </div>
      </>
    );
  }

  const { formation, progress } = current;
  const quiz = (formation.FormationQuizzes || []).find((q) => String(q.id) === String(quizId));

  if (!quiz) {
    return (
      <>
        <Nav socket={socket} />
        <div className="mainContainer">
          <div className="lesson-fullscreen">
            <p style={{ padding: "2rem" }}>Quizz introuvable.</p>
          </div>
        </div>
      </>
    );
  }

  // Anti-triche : un quizz n'est accessible que si son module (resp. tous les modules) est terminé
  const modules = sortByOrder(formation.FormationModules);
  const completedSet = new Set(progress.completedLessonIds || []);
  // Un module sans leçon est ignoré dans le déverrouillage (complet par convention),
  // même règle que le backend : il ne bloque jamais le quizz final.
  const isModuleComplete = (m) => {
    const ls = (m && m.FormationLessons) || [];
    return ls.length === 0 || ls.every((l) => completedSet.has(l.id));
  };
  const unlocked = quiz.moduleId
    ? isModuleComplete(modules.find((m) => m.id === quiz.moduleId) || {})
    : modules.length > 0 && modules.every(isModuleComplete);

  if (!unlocked) {
    return (
      <>
        <Nav socket={socket} />
        <div className="mainContainer">
          <div className="lesson-fullscreen">
            <div className="lesson-layout">
              <CurriculumSidebar formation={formation} progress={progress} active={{ type: "quiz", id: quizId }} />
              <main className="lesson-main">
                <Link to={`/formations/${id}`} className="lesson-back">
                  <ArrowLeft size={15} /> Retour à la formation
                </Link>
                <div className="quiz-locked-notice">
                  <Lock size={40} />
                  <p>
                    {quiz.moduleId
                      ? "Terminez toutes les leçons du module pour accéder à ce quizz."
                      : "Terminez tous les modules de la formation pour accéder au quizz final."}
                  </p>
                </div>
              </main>
            </div>
          </div>
        </div>
      </>
    );
  }

  const questions = sortByOrder(quiz.FormationQuestions);
  const passedState = progress.quizState && progress.quizState[quiz.id];
  const alreadyPassed = !!(passedState && passedState.passed);

  // Étape suivante dans le curriculum après ce quizz (leçon ou quizz)
  const steps = [];
  modules.forEach((m) => {
    sortByOrder(m.FormationLessons).forEach((l) => steps.push({ type: "lesson", id: l.id }));
    const mq = (formation.FormationQuizzes || []).find((q) => q.moduleId === m.id);
    if (mq) steps.push({ type: "quiz", id: mq.id });
  });
  const finalQ = (formation.FormationQuizzes || []).find((q) => !q.moduleId);
  if (finalQ) steps.push({ type: "quiz", id: finalQ.id });
  const curIdx = steps.findIndex((s) => s.type === "quiz" && String(s.id) === String(quizId));
  const nextStep = curIdx >= 0 ? steps[curIdx + 1] : null;
  const nextHref = nextStep
    ? nextStep.type === "lesson"
      ? `/formations/${id}/apprendre/${nextStep.id}`
      : `/formations/${id}/quizz/${nextStep.id}`
    : `/formations/${id}`;
  const nextLabel = nextStep ? "Continuer" : "Retour à la formation";

  const toggle = (q, optId) => {
    setAnswers((prev) => {
      const cur = prev[q.id] || [];
      if (q.type === "multiple") {
        return { ...prev, [q.id]: cur.includes(optId) ? cur.filter((x) => x !== optId) : [...cur, optId] };
      }
      return { ...prev, [q.id]: [optId] };
    });
  };

  const submit = async () => {
    const payload = questions.map((q) => ({ questionId: q.id, selectedOptionIds: answers[q.id] || [] }));
    try {
      const r = await dispatch(attemptQuiz(quiz.id, payload));
      setResult(r);
      if (r && r.completion && r.completion.complete) setCelebrate(true);
    } catch (e) {
      toast.error(e.response?.data?.message || "Impossible de soumettre le quizz.");
    }
  };

  return (
    <>
      {celebrate ? <Confetti onDone={() => setCelebrate(false)} /> : null}
      <ToastContainer />
      <Nav socket={socket} />
      <div className="mainContainer">
        <div className="lesson-fullscreen">
          <div className="lesson-layout">
            <CurriculumSidebar formation={formation} progress={progress} active={{ type: "quiz", id: quizId }} />
            <main className="lesson-main">
              <Link to={`/formations/${id}`} className="lesson-back">
                <ArrowLeft size={15} /> Retour à la formation
              </Link>
              <h1 className="quiz-page-title">{quiz.title}</h1>

              {result ? (
            <div className={`quiz-result-card ${result.passed ? "pass" : "fail"}`}>
              <div className="quiz-result-icon">
                {result.passed ? <Trophy size={42} /> : <RotateCcw size={42} />}
              </div>
              <h2 className="quiz-result-title">
                {result.passed ? "Bravo, quizz réussi !" : "Pas encore réussi"}
              </h2>
              <div className="quiz-result-score">
                <span className="quiz-result-pct">{result.score}%</span>
                <span className="quiz-result-detail">
                  {result.correct}/{result.total} bonnes réponses
                </span>
              </div>
              <p className="quiz-result-msg">
                {result.passed
                  ? "Tu as validé ce quizz. Continue sur ta lancée !"
                  : `Il te faut au moins ${result.threshold}% pour valider. Revois les leçons puis retente ta chance.`}
              </p>
              <div className="quiz-result-actions">
                {result.passed ? (
                  <Link to={nextHref} className="greenButton quiz-result-btn">
                    {nextLabel} <ArrowRight size={16} />
                  </Link>
                ) : (
                  <>
                    <button
                      className="greenButton quiz-result-btn"
                      onClick={() => {
                        setResult(null);
                        setAnswers({});
                      }}
                    >
                      <RotateCcw size={16} /> Recommencer
                    </button>
                    <Link to={`/formations/${id}`} className="blackButton quiz-result-btn">
                      Retour à la formation
                    </Link>
                  </>
                )}
              </div>
            </div>
          ) : alreadyPassed && !retake ? (
            <div className="quiz-result-card pass">
              <div className="quiz-result-icon">
                <Trophy size={42} />
              </div>
              <h2 className="quiz-result-title">Quizz déjà validé</h2>
              <div className="quiz-result-score">
                <span className="quiz-result-pct">{passedState.score}%</span>
                <span className="quiz-result-detail">Meilleur score</span>
              </div>
              <p className="quiz-result-msg">
                Tu as déjà réussi ce quizz. Tu peux le refaire si tu veux t'entraîner.
              </p>
              <div className="quiz-result-actions">
                <Link to={nextHref} className="greenButton quiz-result-btn">
                  {nextLabel} <ArrowRight size={16} />
                </Link>
                <button
                  className="blackButton quiz-result-btn"
                  onClick={() => {
                    setAnswers({});
                    setRetake(true);
                  }}
                >
                  <RotateCcw size={16} /> Refaire le quizz
                </button>
              </div>
            </div>
          ) : (
            <>
              {questions.map((q, qi) => (
                <div className="quiz-question" key={q.id}>
                  <p className="quiz-q-text">
                    {qi + 1}. {q.text}
                  </p>
                  {(q.options || []).map((o) => (
                    <label key={o.id} className="quiz-option">
                      <input
                        type={q.type === "multiple" ? "checkbox" : "radio"}
                        name={`q-${q.id}`}
                        checked={(answers[q.id] || []).includes(o.id)}
                        onChange={() => toggle(q, o.id)}
                      />
                      {o.text}
                    </label>
                  ))}
                </div>
              ))}
              <button className="greenButton" onClick={submit} disabled={!questions.length}>
                Valider mes réponses
              </button>
            </>
          )}
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormationQuiz;
